import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { StatusIconComponent, TooltipDirective } from '@mode/capra';
import { formatBytes } from '@mode/shared/util-js';
import { SharedUtilSegmentModule } from '@mode/shared/util-segment';
import { BehaviorSubject } from 'rxjs';

@Component({
  standalone: true,
  selector: 'mode-dataset-metadata',
  imports: [CommonModule, StatusIconComponent, TooltipDirective, SharedUtilSegmentModule],
  templateUrl: './dataset-metadata.component.html',
  styleUrls: ['./dataset-metadata.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetMetadataComponent implements OnChanges {
  @Input() isSheetDataset = false;
  @Input() isNotebookCell = false;
  @Input() hasCalcFields = false;
  @Input({ required: true }) rowCount = 0;
  @Input({ required: true }) columnCount = 0;
  @Input({ required: true }) size = 0;
  @Input({ required: true }) connection = '';
  @Input({ required: true }) collection = '';
  @Input({ required: true }) schedules = '';

  @Output() addScheduleClick = new EventEmitter<void>();

  dataSource$ = new BehaviorSubject<string>('');
  formattedBytesSize$ = new BehaviorSubject<string>('');

  tooltipButtonSelectors = ['.open-schedule-button'];
  readonly calcFieldsWarning = `<span class='status-tooltip'>This Dataset contains 1 or more calculated fields with aggregate or analytical functions that aren’t supported in the imported table.</span>`;
  noScheduleWarning = `<div class="status-tooltip-open-schedule">
      <span class='status-tooltip'>Without a schedule, content built from this data won’t receive fresh results unless the Dataset is run manually via the Datasets editor.</span>
      <button class="open-schedule-button"
      >Add a schedule</button>
    </div>`;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['size']?.currentValue) {
      this.formattedBytesSize$.next(formatBytes(changes['size']?.currentValue));
    }

    if (changes['isNotebookCell']?.currentValue) {
      const scheduleMessage = this.isNotebookCell
        ? 'Without a Report schedule, content built from this data won’t receive fresh results unless the source Report or Notebook is run manually.'
        : 'Without a schedule, content built from this data won’t receive fresh results unless the Dataset is run manually via the Datasets editor.';

      const buttonText = this.isNotebookCell ? 'Add a Report schedule before publishing' : 'Add a schedule';

      this.noScheduleWarning = `<div class="status-tooltip-open-schedule"><span class="status-tooltip">${scheduleMessage}</span><button class="open-schedule-button">${buttonText}</button></div>`;
    }

    if (changes['isSheetDataset'] || changes['isNotebookCell']) {
      let dataSource = 'SQL Query';
      if (changes['isSheetDataset']?.currentValue) {
        dataSource = 'Google Sheet Integration';
      } else if (changes['isNotebookCell']?.currentValue) {
        dataSource = 'Python output';
      }
      this.dataSource$.next(dataSource);
    }
  }
}
